/**
 * The signed-in principal. One query (`GET /auth/me`) backs the whole console:
 * navigation visibility, RBAC gates and the user menu all read it.
 */
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { ApiError, authApi } from './api.js';
import type { Me, Role } from './schemas.js';

export const SESSION_KEY = ['auth', 'me'] as const;

const ROLE_RANK: Record<Role, number> = { owner: 4, admin: 3, operator: 2, viewer: 1 };

/** True when `role` is at least `minimum` in the RBAC ladder. */
export function roleAtLeast(role: Role | undefined, minimum: Role): boolean {
  if (!role) return false;
  return ROLE_RANK[role] >= ROLE_RANK[minimum];
}

export function useSession(): UseQueryResult<Me, Error> {
  return useQuery({
    queryKey: SESSION_KEY,
    queryFn: () => authApi.me(),
    staleTime: 60_000,
    retry: (failureCount, error) => {
      if (error instanceof ApiError && error.status < 500) return false;
      return failureCount < 1;
    },
  });
}

/** The current role, or undefined while the session is loading. */
export function useRole(): Role | undefined {
  return useSession().data?.role;
}

/** Convenience gate for conditional UI (`useCan('admin')`). */
export function useCan(minimum: Role): boolean {
  return roleAtLeast(useRole(), minimum);
}
